// The debug menu's tools (docs/05-Tools/Debug Menu.md), for testers on
// staging and for development: clear this account's data, the shown night's,
// or the night's horoscope, through the Worker, which refuses on production.
// After a clear the browser forgets what it kept for the cleared data (the
// horoscope picks, the puzzle extra words, the constellations the Horoscope
// button's star has seen, and for a player clear the How to Play card's count)
// and the night shown reloads.
import { api } from '../api/client';
import type { DebugCleared, DebugScope } from '../api/types';
import { allLevels, loadNight, nightStore } from '../night/nightStore';
import { HOROSCOPE_SEEN_KEY } from '../horoscope/horoscopeNotice';
import { HOW_TO_PLAY_KEY } from '../settings/howToPlay';

export type { DebugScope } from '../api/types';

const PICKS_PREFIX = 'wits.horoscope.picks.';
const EXTRA_WORDS_PREFIX = 'wits.extraWords.';

/** Which of this browser's keys belong to the cleared data. */
export function keysToForget(scope: DebugScope, date: string | null, keys: readonly string[], playIds: readonly string[]): string[] {
  return keys.filter((key) => {
    if (key === HOW_TO_PLAY_KEY) return scope === 'player';
    if (key === HOROSCOPE_SEEN_KEY) return true;
    if (key.startsWith(PICKS_PREFIX)) return scope === 'player' || key === `${PICKS_PREFIX}${date}`;
    if (key.startsWith(EXTRA_WORDS_PREFIX)) return scope === 'player' || (scope === 'night' && playIds.includes(key.slice(EXTRA_WORDS_PREFIX.length)));
    return false;
  });
}

function forgetLocal(scope: DebugScope, date: string | null, playIds: readonly string[]): void {
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) keys.push(key);
    }
    for (const key of keysToForget(scope, date, keys, playIds)) localStorage.removeItem(key);
  } catch {
    // Private mode or blocked storage: nothing was kept.
  }
}

const count = (n: number, noun: string) => `${n} ${noun}${n === 1 ? '' : 's'}`;

/** "Cleared 3 plays, 1 share and 1 horoscope.", or that there was nothing. */
export function describeCleared(cleared: DebugCleared): string {
  const parts: string[] = [];
  if (cleared.plays) parts.push(count(cleared.plays, 'play'));
  if (cleared.shares) parts.push(count(cleared.shares, 'share'));
  if (cleared.horoscopes) parts.push(count(cleared.horoscopes, 'horoscope'));
  if (parts.length === 0) return 'Nothing to clear.';
  const last = parts.pop()!;
  return `Cleared ${parts.length ? `${parts.join(', ')} and ${last}` : last}.`;
}

/**
 * Clear through the Worker, forget the browser's part, reload the night shown;
 * resolves to a line saying what went. `date` is the past night shown, or
 * null for tonight.
 */
export async function clearData(scope: DebugScope, date: string | null): Promise<string> {
  const cleared = await api.debugClear(scope, date);
  const night = nightStore.get().night;
  const playIds = night ? allLevels(night).flatMap((v) => (v.playId ? [v.playId] : [])) : [];
  forgetLocal(scope, cleared.date, playIds);
  await loadNight(nightStore.get().requested);
  return describeCleared(cleared);
}
